import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";

import { Token } from "../models/token";
import { APP_CONFIG, AppConfig } from "../../config";
//import { Router } from "@angular/router";
import { ClientService } from "../../client.service";
import { AccountService } from "../../account.service";
import { StorageService } from "../../storage.service";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private url: string;
  private clientId: string;
  private secret: string;
  public currentTokenSubject: BehaviorSubject<Token | null>;

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  private refreshSessionRequest?: Observable<boolean>;

  constructor(
    private storage: StorageService,
    private http: HttpClient,
    //private router: Router,
    private clientService: ClientService,
    private accountService: AccountService,
    @Inject(APP_CONFIG) config: AppConfig,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.url = config.apiEndpoint;
    this.clientId = config.clientId;
    this.secret = config.secret;

    if (isPlatformBrowser(this.platformId)) {
      const token = this.storage.getItem("currentToken");
      this.currentTokenSubject = new BehaviorSubject<Token | null>(
        token ? JSON.parse(token) : null,
      );
    } else {
      this.currentTokenSubject = new BehaviorSubject<Token | null>(null);
    }
  }

  public get getCurrentToken(): Token | null {
    return this.currentTokenSubject.value;
  }

  /**
   * @description log the user in
   * @param {string} username user email or username
   * @param {string} password user password
   */
  login(username: string, password: string): Observable<string> {
    return this.http
      .post<string>(
        `${this.url}/auth/login`,
        {
          email: username,
          password: password,
        },
        { withCredentials: true, responseType: "text" as "json" },
      )
      .pipe(
        map((response: string) => {
          if (response) {
            this.accountService.refreshAccount();
            this.isAuthenticatedSubject$.next(true);
          }
          return response;
        }),
      );
  }

  loginFacebook(accessToken: string, facebookId: string) {
    this.logout();

    return this.http
      .post<Token>(`${this.url}/auth/login-facebook`, {
        accessToken,
        facebookId,
      })
      .pipe(
        map((response: Token) => {
          if (response && response.accessToken) {
            if (isPlatformBrowser(this.platformId)) {
              const dateNow = new Date();
              this.storage.setItem(
                "dateStartToken",
                JSON.stringify(dateNow.getTime()),
              );
              this.storage.setItem("currentToken", JSON.stringify(response));
            }
            this.currentTokenSubject.next(response);
          }
          return response;
        }),
      );
  }

  loginOauth2(redirectUri: string) {
    this.logout();
    const dateNow = new Date();
    this.storage.setItem(
      "dateStartTokenOauth2",
      JSON.stringify(dateNow.getTime()),
    );
    window.location.href =
      `${this.url}/oauth2/authorize/google?redirect_uri=` + redirectUri;
  }
  loginFACEBOOK_Oauth2(redirectUri: string) {
    this.logout();
    const dateNow = new Date();
    this.storage.setItem(
      "dateStartTokenOauth2",
      JSON.stringify(dateNow.getTime()),
    );
    console.log(redirectUri);
  }
  /**
   * @description log the user out
   */
  logout(): Observable<any> {
    return this.http
      .post(
        `${this.url}/auth/logout`,
        {},
        {
          withCredentials: true,
          responseType: "text" as const,
        },
      )
      .pipe(
        map(() => {
          this.accountService.update(null);
          this.isAuthenticatedSubject$.next(false);
        }),
      );
  }

  /**
   * @description refresh user session
   */

  refreshSession(): Observable<boolean> {
    if (this.refreshSessionRequest) {
      return this.refreshSessionRequest;
    }

    const refreshRequest = this.http
      .post(
        `${this.url}/auth/refresh-token`,
        {},
        {
          withCredentials: true,
          responseType: "text" as "text",
        },
      )
      .pipe(
        map(() => {
          this.accountService.refreshAccount();
          this.isAuthenticatedSubject$.next(true);
          return true;
        }),
        catchError(() => {
          this.accountService.update(null);
          this.isAuthenticatedSubject$.next(false);
          return of(false);
        }),
        finalize(() => {
          this.refreshSessionRequest = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.refreshSessionRequest = refreshRequest;
    return refreshRequest;
  }

  /**
   * @description get a new account confirmation mail
   * @param {string} email user email
   */
  getConfirmationMail(email: string): Observable<boolean> {
    const url = `${this.url}/new/clients/${email}/email`;
    return this.http.get<boolean>(url);
  }
}
